import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { bulkActionsAPI } from '../services/api';
import { runBulkActionWithProgress } from '../utils/bulkActionRunner';

/**
 * Bulk action progress, owned above the page.
 *
 * The goal this exists for, in the user's words: "user can work on other things while processing
 * the order — switch tabs etc." That single sentence decides the architecture. While progress
 * lived in OrdersPage, navigating away unmounted it and the operator was pinned to one screen for
 * the length of a 500-order batch. So it lives in the app shell instead.
 *
 * Two consequences worth stating, because both were mistakes in an earlier draft of this feature:
 *
 *   1. Progress is a MAP keyed by jobId, not one object. "Ship 100, minimise, ship 50 more" is a
 *      normal thing to do, and with a single object the second run silently overwrites the first.
 *
 *   2. Completion is owned HERE, not by the page that started the job. The page is unmounted the
 *      moment the user navigates away — which is the one behaviour this feature guarantees will
 *      happen — so anything that runs on completion has to survive it.
 *
 * The jobs themselves are server-side and entirely unaffected by any of this; polling lives in
 * bulkActionRunner's own timers. Minimising, navigating, or closing a card changes what is on
 * screen and nothing else.
 */
const BulkProgressContext = createContext(null);

export const useBulkProgress = () => {
  const ctx = useContext(BulkProgressContext);
  if (!ctx) throw new Error('useBulkProgress must be used inside <BulkProgressProvider>');
  return ctx;
};

// How often to look for batches started by OTHER staff on the same account. Deliberately not
// aggressive: unconditional polling on every open tab is precisely what produced the A40 read-cost
// leak, and although MongoDB makes this query cheap the discipline is worth keeping.
const PEER_POLL_MS = 10000;

export function BulkProgressProvider({ children }) {
  // jobId -> job state. Plain object rather than a Map so React's state identity rules stay obvious.
  const [jobs, setJobs] = useState({});
  // Which job's full modal is open, if any. Only ever one at a time.
  const [expandedId, setExpandedId] = useState(null);

  const patchJob = useCallback((jobId, patch) => {
    if (!jobId) return;
    setJobs((prev) => {
      const cur = prev[jobId];
      if (!cur && !patch.__create) return prev;   // a stale poll for a dismissed card
      return { ...prev, [jobId]: { ...cur, ...patch, jobId } };
    });
  }, []);

  const dismiss = useCallback((jobId) => {
    setJobs((prev) => { const n = { ...prev }; delete n[jobId]; return n; });
    setExpandedId((cur) => (cur === jobId ? null : cur));
  }, []);

  const minimize = useCallback((jobId) => {
    patchJob(jobId, { minimized: true });
    setExpandedId((cur) => (cur === jobId ? null : cur));
  }, [patchJob]);

  const expand = useCallback((jobId) => {
    patchJob(jobId, { minimized: false });
    setExpandedId(jobId);
  }, [patchJob]);

  // Callers register what to do when a job finishes. Held in a ref, not state, because the page
  // that supplied it may already be unmounted and we must not trigger a render into a dead tree.
  const doneHandlers = useRef({});

  /**
   * Start a bulk action and track it. Returns the runner's promise so a caller that is still
   * mounted can also react locally (refresh its list, clear its selection).
   *
   * `onDone` is invoked with the finished job. It runs even if the page has unmounted, so it must
   * be safe to call from anywhere — anything touching page state has to guard for that itself.
   */
  const start = useCallback((type, orderIds, meta, { label, extra = {}, onDone } = {}) => {
    let myJobId = null;
    return runBulkActionWithProgress(type, orderIds, meta, {
      label,
      extra,
      keepOnDone: true,          // this provider decides how a finished job is presented, not the runner
      onJobId: (id) => {
        myJobId = id;
        if (onDone) doneHandlers.current[id] = onDone;
        setJobs((prev) => ({
          ...prev,
          [id]: {
            jobId: id, type, label, ...extra,
            current: 0, total: orderIds.length, ok: 0, fail: 0,
            done: false, minimized: false, mine: true,
            orderIds, detailIds: [],
          },
        }));
        setExpandedId(id);
      },
      setProgress: (p) => {
        if (!p) return;                       // runner only nulls when it owns presentation; it does not here
        const id = p.jobId || myJobId;
        if (!id) return;                      // pre-create tick: the job document does not exist yet
        patchJob(id, { ...p, jobId: id });
        if (p.done) setExpandedId((cur) => (cur === id ? null : cur));   // never leave a finished modal blocking the screen
      },
      onDone: (job) => {
        const id = myJobId;
        if (id) patchJob(id, { done: true, job });
        const h = id && doneHandlers.current[id];
        if (h) { delete doneHandlers.current[id]; try { h(job); } catch { /* page may be gone */ } }
      },
    });
  }, [patchJob]);

  // ── batches started by other staff on this account ──────────────────────────────────────
  //
  // No new endpoint is needed: bulk_actions are keyed by TENANT rather than by person
  // (req.user.id always means the account), so the existing list already returns everyone's jobs
  // and carries created_by_name. Peer jobs are shown read-only — they lock the orders they are
  // shipping and say who is shipping them, but cannot be dismissed from here.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;   // see PEER_POLL_MS
      try {
        const res = await bulkActionsAPI.list();
        if (!alive) return;
        const running = (res.data || []).filter((j) => j.status === 'processing');
        setJobs((prev) => {
          const next = { ...prev };
          const seen = new Set();
          for (const j of running) {
            seen.add(j.id);
            if (next[j.id] && next[j.id].mine) continue;     // our own job: the runner is authoritative
            const p = j.progress || {};
            next[j.id] = {
              ...(next[j.id] || {}),
              jobId: j.id,
              type: j.type,
              label: j.type === 'bulk_ship' ? 'Creating shipments' : 'Preparing labels',
              current: p.current ?? 0,
              total: p.total ?? (j.order_ids || []).length,
              ok: p.ok ?? 0, fail: p.fail ?? 0,
              done: false, mine: false,
              minimized: true,                                // never open someone else's job over your work
              byName: j.created_by_name || 'Someone',
              orderIds: j.order_ids || [],
              detailIds: ((j.summary && j.summary.details) || []).map((d) => d.order_id),
            };
          }
          // Drop peer jobs that have finished. Our own jobs are left alone — they stay until the
          // operator dismisses them, which is the whole point of the "View summary" state.
          for (const id of Object.keys(next)) {
            if (!next[id].mine && !seen.has(id)) delete next[id];
          }
          return next;
        });
      } catch { /* transient: the next tick tries again */ }
    };
    tick();
    const t = setInterval(tick, PEER_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, []);

  /**
   * Orders currently inside a running batch — the union across every active job, ours and peers'.
   *
   * An order leaves this set as soon as IT is reported in summary.details, not when the batch
   * finishes, so rows unlock continuously through a long run.
   *
   * Why the row checkbox is disabled and not just the Ship button: double-shipping is ALREADY
   * impossible (shipOrder returns 409 "already shipped"). What actually goes wrong is that a
   * second batch containing an in-flight order gets that 409 back and records it as a FAILURE, so
   * the summary reports failures that are not failures. This set exists to stop that, not to
   * protect the data — the 409 does that and must not be removed on the assumption this replaces it.
   */
  const inFlightRef = useRef(new Set());
  const inFlightOrderIds = useMemo(() => {
    const set = new Set();
    for (const j of Object.values(jobs)) {
      if (j.done) continue;
      const reported = new Set(j.detailIds || []);
      for (const id of j.orderIds || []) if (!reported.has(id)) set.add(id);
    }
    // Return the PREVIOUS Set when membership is unchanged. The progress poll fires every 1.2s and
    // a fresh Set each time is a new identity, which re-rendered the whole order table — visible as
    // a flicker on a 578-row selection. Only a real membership change should cost a render.
    const prev = inFlightRef.current;
    if (prev.size === set.size) {
      let same = true;
      for (const id of set) if (!prev.has(id)) { same = false; break; }
      if (same) return prev;
    }
    inFlightRef.current = set;
    return set;
  }, [jobs]);

  // Is one of MY batches still running? Drives hiding the bulk-action bar: offering "Bulk Ship" and
  // "Cancel Orders" while a batch is mid-flight invites a second batch over the same orders.
  const hasRunningJob = useMemo(
    () => Object.values(jobs).some((j) => j.mine && !j.done),
    [jobs],
  );

  // Who is shipping a given order, for the row tooltip. Empty for your own batches — you know.
  const inFlightOwner = useCallback((orderId) => {
    for (const j of Object.values(jobs)) {
      if (j.done || j.mine) continue;
      if ((j.orderIds || []).includes(orderId)) return j.byName || 'Someone';
    }
    return null;
  }, [jobs]);

  const value = useMemo(() => ({
    jobs, expandedId, start, minimize, expand, dismiss, inFlightOrderIds, inFlightOwner, hasRunningJob,
  }), [jobs, expandedId, start, minimize, expand, dismiss, inFlightOrderIds, inFlightOwner, hasRunningJob]);

  return <BulkProgressContext.Provider value={value}>{children}</BulkProgressContext.Provider>;
}
